import pokemons from "../pokemons";
import maps from "../maps";
import neutrals from "../neutrals";
import battleItems from "../battleItems";
import heldItems from "../heldItems";

/** Slug from roster image path: "pokemons/roster-venusaur.webp" -> "venusaur" */
function slugFromMain(mainPath: string): string {
  return mainPath
    .replace(/^pokemons\/roster-/, "")
    .replace(/\.(webp|png)$/i, "");
}

const names: Record<string, string> = {};
for (const p of pokemons) {
  names[slugFromMain(p.images.main)] = p.name;
}
for (const m of maps) {
  names[m.id] = m.name;
  if (m.description) names[`${m.id}.description`] = m.description;
}
for (const n of neutrals) {
  names[n.id] = n.name;
}
for (const b of battleItems) {
  names[`battle-item.${b.id}`] = b.name;
}
for (const h of heldItems) {
  names[`held-item.${h.id}`] = h.name;
}

// Aliases for slug variants (e.g. kebab-case "mega-mewtwo-x" from display name).
names["mega-mewtwo-x"] = "Mega Mewtwo X";
names["mega-mewtwo-y"] = "Mega Mewtwo Y";

// Spawn info (HTML). Other locales override same keys for translations.
Object.assign(names, {
  "spawn.info.groudon.boss":
    "<p>When defeated, all team members alive receive a <strong>buff</strong> and <strong>15 aeos points</strong>.<br>This buff is <strong>permanent until the holder is KO'd</strong>. When the holder is <strong>KO'd</strong>, the buff <strong>transfers to the killer</strong>.<br>It provides a <strong>goal-scoring speed boost</strong>, a <strong>huge damage boost</strong>, and a <strong>small shield</strong>.<br>Always spawn at 2:00.</p>",
  "spawn.info.bunnelby.initial":
    "<p>They <strong>spawn at the beginning of the game</strong> and <strong>never respawn</strong> once killed.</p><p>Give 2 points.</p>",
  "spawn.info.bunnelby.lane_940":
    "<p>Starts spawning at <strong>9:40</strong>.</p><p>When defeated, grants <strong>2 points</strong> and respawn after <strong>60 seconds</strong>.</p><p>If either of the <strong>first towers</strong> in the lane is destroyed, it <strong>disappears</strong>.</p>",
  "spawn.info.bunnelby.lane_920":
    "<p>Starts spawning at <strong>9:20</strong>.</p><p>When defeated, grants <strong>2 points</strong> and respawn after <strong>60 seconds</strong>.</p><p>If either of the <strong>first towers</strong> in the lane is destroyed, it <strong>disappears</strong>.</p>",
  "spawn.info.bunnelby.lane_915":
    "<p>Starts spawning at <strong>9:15</strong>.</p><p>When defeated, grants <strong>2 points</strong> and respawn after <strong>60 seconds</strong>.</p><p>If either of the <strong>first towers</strong> in the lane is destroyed, it <strong>disappears</strong>.</p>",
  "spawn.info.accelgor":
    "<p>Starts spawning at <strong>9:45</strong>.</p><p>When defeated, grants <strong>4 / 7 points</strong> and a <strong>buff</strong>. <strong>Respawns after 60 seconds</strong>.</p><p>This buff lasts for <strong>70 seconds</strong> and provides a <strong>10% move cooldown reduction</strong>.</p>",
  "spawn.info.escavalier":
    "<p>Starts spawning at <strong>9:45</strong>.</p><p>When defeated, grants <strong>4 / 7 points</strong> and a <strong>buff</strong>. <strong>Respawns after 60 seconds</strong>.</p><p>This buff lasts for <strong>70 seconds</strong> and provides <strong>basic attacks extra damage and decreases opponent movement speed for a short time</strong>.</p>",
  "spawn.info.baltoy.generic":
    "<p>Starts spawning at <strong>9:45</strong>.</p><p>When defeated, grants <strong>2 points</strong>. <strong>Respawns after 60 seconds</strong>.</p>",
  "spawn.info.baltoy.lane_940":
    "<p>Starts spawning at <strong>9:40</strong>.</p><p>When defeated, grants <strong>2 points</strong>. <strong>Respawns after 60 seconds</strong>.</p><p>If either of the <strong>first towers</strong> in the lane is destroyed, it <strong>disappears</strong>.</p>",
  "spawn.info.baltoy.lane_920":
    "<p>Starts spawning at <strong>9:40</strong>.</p><p>When defeated, grants <strong>2 points</strong>. <strong>Respawns after 60 seconds</strong>.</p><p>If either of the <strong>first towers</strong> in the lane is destroyed, it <strong>disappears</strong>.</p>",
  "spawn.info.baltoy.lane_910":
    "<p>Starts spawning at <strong>9:10</strong>.</p><p>When defeated, grants <strong>2 points</strong>. <strong>Respawns after 60 seconds</strong>.</p><p>If either of the <strong>first towers</strong> in the lane is destroyed, it <strong>disappears</strong>.</p>",
  "spawn.info.baltoy.lane_910_only":
    "<p>Starts spawning at <strong>9:10</strong>.</p><p>When defeated, grants <strong>2 points</strong>. <strong>Respawns after 60 seconds</strong>.</p>",
  "spawn.info.baltoy.lane_840":
    "<p>Starts spawning at <strong>8:40</strong>.</p><p>When defeated, grants <strong>2 points</strong>. <strong>Respawns after 60 seconds</strong>.</p>",
  "spawn.info.baltoy.tower":
    "<p>Starts spawning when the corresponding <strong>first tower is destroyed</strong>.</p><p>When defeated, grants <strong>2 points</strong>. <strong>Respawns after 60 seconds</strong>.</p>",
  "spawn.info.regidrago":
    "<p>Spawns in the <strong>middle of the map at 8:00</strong>. When killed, <strong>Altaria spawn instead 1:30 minute after</strong>.<br>The team that defeats <strong>Regidrago</strong> gains <strong>experience for all teammates</strong>.<br>The player who lands the killing blow receives a <strong>scoring-speed buff</strong> and <strong>20 points</strong>.</p>",
  "spawn.info.regieleki":
    "<p>Spawns at <strong>7:00</strong> on a random lane between <strong>toplane and botlane</strong>.<br>When defeated, goes to the base of the opposing team and if it touches the base, scores <strong>20/16/12/8 points</strong> and if the base is still alive, makes <strong>any goal become instant during 20/15/10/5s</strong>.<br>The player who lands the killing blow receives <strong>20 points</strong>.<br>Respawns <strong>2 minutes</strong> after death.</p>",
  "spawn.info.regi.700":
    "<p>Spawns at <strong>7:00</strong>.</p>",
  "spawn.info.xatu":
    "<p>Spawns at <strong>9:59</strong>.</p><p>Grants <strong>a large amount of experience</strong> and <strong>2 points</strong> when defeated.</p><p><strong>Does not respawn</strong>.</p>",
  "spawn.info.natu.900":
    "<p>Spawns at <strong>9:00</strong>.</p>",
  "spawn.info.natu.920":
    "<p>Spawns at <strong>9:20</strong>.</p>",
  "spawn.info.indeedee.lane":
    "<p>Starts spawning at <strong>9:30</strong> / <strong>9:35</strong>.</p><p>When defeated, <strong>respawns after 60 seconds</strong> and grants <strong>5 points</strong>.</p><p>If either of the <strong>first towers</strong> in the lane is destroyed, it <strong>disappears</strong>.</p>",
  "spawn.info.indeedee.tower":
    "<p>Spawns when the corresponding <strong>first tower</strong> is destroyed.</p><p>When defeated, <strong>respawns after 60 seconds</strong> and grants <strong>5 points</strong>.</p>",
  "spawn.info.salac":
    "<p>When consumed, grants a <strong>movement speed bonus</strong> for <strong>3 seconds</strong>.</p><p>Starts spawning at <strong>8:30</strong> and respawns <strong>60 seconds</strong> after being consumed.</p>",
  "spawn.info.salac.short":
    "<p>Movement speed bonus for 3 seconds.</p>",
  "spawn.info.altaria.after_regidrago":
    "<p>Starts spawning <strong>mid 1:30 after Regidrago is killed</strong>.</p>",
  "spawn.info.altaria.rayquaza":
    "<p>Spawn at <strong>8:00</strong> and respawn every <strong>1:30</strong>.</p><p>Disappears at <strong>2:30</strong>.</p>",
  "spawn.info.kyogre.boss":
    "<p>When defeated, all team members alive receive a <strong>buff</strong>, <strong>15 aeos points</strong> and all opponents' towers still up are disabled for 1:30 (opponent can't receive shield and regen).<br>This buff is <strong>permanent until the holder is KO'd</strong>. When the holder is <strong>KO'd</strong>, the buff <strong>transfers to the killer</strong>.<br>It provides a <strong>goal-scoring speed boost</strong>, <strong>self regen (5% per second)</strong>, and a <strong>small shield</strong>.<br>Always spawn at 2:00.</p>",
  "spawn.info.rayquaza.boss":
    "<p>When defeated, grants the winning team <strong>15 aeos points</strong>, a <strong>shield</strong> that <strong>speeds up goal scoring</strong> and <strong>prevents goals from being blocked</strong>.<br>Always spawns at 2:00.</p>",
  "spawn.info.regidrago.kyogre":
    "<p>Spawns in the <strong>middle of the map at 8:00</strong>.</p>",
  "spawn.info.xatu.short":
    "<p>Spawns at <strong>9:59</strong>.</p>",
  "spawn.info.indeedee.tower_break":
    "<p>Tower break spawn.</p>",
  "spawn.info.baltoy.tower_break":
    "<p>Tower break spawn.</p>",
  "spawn.info.bunnelby.lane":
    "<p>Starts spawning at <strong>9:40</strong>.</p>",
  "spawn.info.bunnelby.lane_920_short":
    "<p>Starts spawning at <strong>9:20</strong>.</p>",
  "spawn.info.bunnelby.lane_915_short":
    "<p>Starts spawning at <strong>9:15</strong>.</p>",
  "spawn.info.baltoy.lane_940_short":
    "<p>Starts spawning at <strong>9:40</strong>.</p>",
  "spawn.info.baltoy.945_short":
    "<p>Starts spawning at <strong>9:45</strong>.</p>",
  "spawn.info.baltoy.lane_910_short":
    "<p>Starts spawning at <strong>9:10</strong>.</p>",
  "spawn.info.baltoy.lane_840_short":
    "<p>Starts spawning at <strong>8:40</strong>.</p>",
  "spawn.info.indeedee.lane_930":
    "<p>Starts spawning at <strong>9:30</strong>.</p>",
  "spawn.info.indeedee.lane_935":
    "<p>Starts spawning at <strong>9:35</strong>.</p>",
  "spawn.info.bunnelby.points":
    "<p>Give 2 points.</p>",
  "spawn.info.baltoy.tower_kyogre":
    "<p>Starts spawning when the corresponding <strong>first tower is destroyed</strong>.</p>",
  "spawn.info.indeedee.tower_short":
    "<p>Spawns when the corresponding <strong>first tower</strong> is destroyed.</p>",
});

// @begin move-names-i18n
Object.assign(names, {
  "move.acid-spray": "Acid Spray",
  "move.acrobatics": "Acrobatics",
  "move.aerial-ace": "Aerial Ace",
  "move.agility": "Agility",
  "move.air-slash": "Air Slash",
  "move.amnesia": "Amnesia",
  "move.anchor-shot": "Anchor Shot",
  "move.ancient-power": "Ancient Power",
  "move.aqua-jet": "Aqua Jet",
  "move.aqua-ring": "Aqua Ring",
  "move.aqua-tail": "Aqua Tail",
  "move.armor-cannon": "Armor Cannon",
  "move.assurance": "Assurance",
  "move.astonish": "Astonish",
  "move.aurora-veil": "Aurora Veil",
  "move.avalanche": "Avalanche",
  "move.baby-doll-eyes": "Baby-Doll Eyes",
  "move.barrier": "Barrier",
  "move.beat-up": "Beat Up",
  "move.belch": "Belch",
  "move.bite": "Bite",
  "move.bitter-blade": "Bitter Blade",
  "move.blaze": "Blaze",
  "move.blaze-kick": "Blaze Kick",
  "move.blizzard": "Blizzard",
  "move.block": "Block",
  "move.bone-rush": "Bone Rush",
  "move.bounce": "Bounce",
  "move.branch-poke": "Branch Poke",
  "move.brave-bird": "Brave Bird",
  "move.brutal-swing": "Brutal Swing",
  "move.bubble": "Bubble",
  "move.bubble-beam": "Bubble Beam",
  "move.bulk-up": "Bulk Up",
  "move.bulldoze": "Bulldoze",
  "move.bullet-punch": "Bullet Punch",
  "move.bullet-seed": "Bullet Seed",
  "move.calm-mind": "Calm Mind",
  "move.charge-beam": "Charge Beam",
  "move.charm": "Charm",
  "move.close-combat": "Close Combat",
  "move.coaching": "Coaching",
  "move.confuse-ray": "Confuse Ray",
  "move.confusion": "Confusion",
  "move.cotton-guard": "Cotton Guard",
  "move.cotton-spore": "Cotton Spore",
  "move.covet": "Covet",
  "move.cross-chop": "Cross Chop",
  "move.curse": "Curse",
  "move.cut": "Cut",
  "move.dark-pulse": "Dark Pulse",
  "move.dark-void": "Dark Void",
  "move.dazzling-gleam": "Dazzling Gleam",
  "move.decorate": "Decorate",
  "move.defense-curl": "Defense Curl",
  "move.detect": "Detect",
  "move.dig": "Dig",
  "move.disable": "Disable",
  "move.disarming-voice": "Disarming Voice",
  "move.discharge": "Discharge",
  "move.dive": "Dive",
  "move.double-hit": "Double Hit",
  "move.double-slap": "Double Slap",
  "move.double-team": "Double Team",
  "move.draco-meteor": "Draco Meteor",
  "move.dragon-breath": "Dragon Breath",
  "move.dragon-cheer": "Dragon Cheer",
  "move.dragon-claw": "Dragon Claw",
  "move.dragon-dance": "Dragon Dance",
  "move.dragon-pulse": "Dragon Pulse",
  "move.dragon-rush": "Dragon Rush",
  "move.dragon-tail": "Dragon Tail",
  "move.draining-kiss": "Draining Kiss",
  "move.dream-eater": "Dream Eater",
  "move.drill-peck": "Drill Peck",
  "move.dual-wingbeat": "Dual Wingbeat",
  "move.dynamic-punch": "Dynamic Punch",
  "move.earthquake": "Earthquake",
  "move.egg-bomb": "Egg Bomb",
  "move.electro-ball": "Electro Ball",
  "move.electro-drift": "Electro Drift",
  "move.electroweb": "Electroweb",
  "move.ember": "Ember",
  "move.eruption": "Eruption",
  "move.explosive-heat-haze": "Explosive Heat Haze",
  "move.extreme-speed": "Extreme Speed",
  "move.fairy-wind": "Fairy Wind",
  "move.fake-out": "Fake Out",
  "move.fake-tears": "Fake Tears",
  "move.feather-dance": "Feather Dance",
  "move.feint": "Feint",
  "move.feint-attack": "Feint Attack",
  "move.fell-stinger": "Fell Stinger",
  "move.fire-blast": "Fire Blast",
  "move.fire-punch": "Fire Punch",
  "move.fire-spin": "Fire Spin",
  "move.flail": "Flail",
  "move.flame-burst": "Flame Burst",
  "move.flame-charge": "Flame Charge",
  "move.flame-wheel": "Flame Wheel",
  "move.flamethrower": "Flamethrower",
  "move.flare-blitz": "Flare Blitz",
  "move.flash-cannon": "Flash Cannon",
  "move.flip-turn": "Flip Turn",
  "move.floral-healing": "Floral Healing",
  "move.flower-trick": "Flower Trick",
  "move.fly": "Fly",
  "move.focus-blast": "Focus Blast",
  "move.follow-me": "Follow Me",
  "move.foul-play": "Foul Play",
  "move.freeze-dry": "Freeze Dry",
  "move.fury-cutter": "Fury Cutter",
  "move.fury-swipes": "Fury Swipes",
  "move.future-sight": "Future Sight",
  "move.giga-drain": "Giga Drain",
  "move.gigaton-hammer": "Gigaton Hammer",
  "move.grass-knot": "Grass Knot",
  "move.grassy-glide": "Grassy Glide",
  "move.gravity": "Gravity",
  "move.growl": "Growl",
  "move.gyro-ball": "Gyro Ball",
  "move.headbutt": "Headbutt",
  "move.heal-pulse": "Heal Pulse",
  "move.heat-wave": "Heat Wave",
  "move.heavy-slam": "Heavy Slam",
  "move.helping-hand": "Helping Hand",
  "move.hex": "Hex",
  "move.high-horsepower": "High Horsepower",
  "move.hone-claws": "Hone Claws",
  "move.horn-leech": "Horn Leech",
  "move.hurricane": "Hurricane",
  "move.hydro-cannon": "Hydro Cannon",
  "move.hydro-pump": "Hydro Pump",
  "move.hyper-beam": "Hyper Beam",
  "move.hyper-voice": "Hyper Voice",
  "move.hyperspace-hole": "Hyperspace Hole",
  "move.hypnosis": "Hypnosis",
  "move.ice-beam": "Ice Beam",
  "move.ice-fang": "Ice Fang",
  "move.ice-hammer": "Ice Hammer",
  "move.ice-shard": "Ice Shard",
  "move.icicle-crash": "Icicle Crash",
  "move.icicle-spear": "Icicle Spear",
  "move.icy-wind": "Icy Wind",
  "move.imprison": "Imprison",
  "move.incinerate": "Incinerate",
  "move.iron-defense": "Iron Defense",
  "move.iron-head": "Iron Head",
  "move.jump-kick": "Jump Kick",
  "move.karate-chop": "Karate Chop",
  "move.knock-off": "Knock Off",
  "move.laser-focus": "Laser Focus",
  "move.lava-plume": "Lava Plume",
  "move.leaf-blade": "Leaf Blade",
  "move.leaf-storm": "Leaf Storm",
  "move.leaf-tornado": "Leaf Tornado",
  "move.leafage": "Leafage",
  "move.leech-life": "Leech Life",
  "move.lick": "Lick",
  "move.light-screen": "Light Screen",
  "move.liquidation": "Liquidation",
  "move.low-sweep": "Low Sweep",
  "move.lunge": "Lunge",
  "move.luster-purge": "Luster Purge",
  "move.mach-punch": "Mach Punch",
  "move.magical-leaf": "Magical Leaf",
  "move.magnet-rise": "Magnet Rise",
  "move.mean-look": "Mean Look",
  "move.mega-punch": "Mega Punch",
  "move.megahorn": "Megahorn",
  "move.metal-claw": "Metal Claw",
  "move.meteor-mash": "Meteor Mash",
  "move.mist-ball": "Mist Ball",
  "move.moonblast": "Moonblast",
  "move.moonlight": "Moonlight",
  "move.muddy-water": "Muddy Water",
  "move.mystical-fire": "Mystical Fire",
  "move.nasty-plot": "Nasty Plot",
  "move.night-shade": "Night Shade",
  "move.night-slash": "Night Slash",
  "move.no-retreat": "No Retreat",
  "move.nuzzle": "Nuzzle",
  "move.outrage": "Outrage",
  "move.overheat": "Overheat",
  "move.pain-split": "Pain Split",
  "move.parabolic-charge": "Parabolic Charge",
  "move.pay-day": "Pay Day",
  "move.payback": "Payback",
  "move.peck": "Peck",
  "move.perish-song": "Perish Song",
  "move.petal-dance": "Petal Dance",
  "move.phantom-force": "Phantom Force",
  "move.play-rough": "Play Rough",
  "move.pollen-puff": "Pollen Puff",
  "move.poltergeist": "Poltergeist",
  "move.pound": "Pound",
  "move.powder-snow": "Powder Snow",
  "move.power-swap": "Power Swap",
  "move.power-up-punch": "Power-Up Punch",
  "move.power-whip": "Power Whip",
  "move.psybeam": "Psybeam",
  "move.psychic": "Psychic",
  "move.psycho-cut": "Psycho Cut",
  "move.psyshock": "Psyshock",
  "move.psystrike": "Psystrike",
  "move.pursuit": "Pursuit",
  "move.pyro-ball": "Pyro Ball",
  "move.quick-attack": "Quick Attack",
  "move.rapid-spin": "Rapid Spin",
  "move.razor-leaf": "Razor Leaf",
  "move.recover": "Recover",
  "move.rest": "Rest",
  "move.rock-polish": "Rock Polish",
  "move.rock-slide": "Rock Slide",
  "move.rock-smash": "Rock Smash",
  "move.rock-tomb": "Rock Tomb",
  "move.rollout": "Rollout",
  "move.sacred-fire": "Sacred Fire",
  "move.sacred-sword": "Sacred Sword",
  "move.safeguard": "Safeguard",
  "move.sand-attack": "Sand Attack",
  "move.sand-tomb": "Sand Tomb",
  "move.scald": "Scald",
  "move.scratch": "Scratch",
  "move.seed-bomb": "Seed Bomb",
  "move.shadow-ball": "Shadow Ball",
  "move.shadow-claw": "Shadow Claw",
  "move.shadow-sneak": "Shadow Sneak",
  "move.shell-smash": "Shell Smash",
  "move.sing": "Sing",
  "move.skull-bash": "Skull Bash",
  "move.sky-attack": "Sky Attack",
  "move.slack-off": "Slack Off",
  "move.slash": "Slash",
  "move.sludge-bomb": "Sludge Bomb",
  "move.smack-down": "Smack Down",
  "move.smart-strike": "Smart Strike",
  "move.smokescreen": "Smokescreen",
  "move.snarl": "Snarl",
  "move.snipe-shot": "Snipe Shot",
  "move.soft-boiled": "Soft-Boiled",
  "move.solar-beam": "Solar Beam",
  "move.solar-blade": "Solar Blade",
  "move.spark": "Spark",
  "move.spirit-shackle": "Spirit Shackle",
  "move.splash": "Splash",
  "move.stealth-rock": "Stealth Rock",
  "move.stomp": "Stomp",
  "move.stone-edge": "Stone Edge",
  "move.stored-power": "Stored Power",
  "move.stuff-cheeks": "Stuff Cheeks",
  "move.submission": "Submission",
  "move.substitute": "Substitute",
  "move.sucker-punch": "Sucker Punch",
  "move.supercell-slam": "Supercell Slam",
  "move.superpower": "Superpower",
  "move.surf": "Surf",
  "move.surging-strikes": "Surging Strikes",
  "move.sweet-kiss": "Sweet Kiss",
  "move.sweet-scent": "Sweet Scent",
  "move.swift": "Swift",
  "move.swords-dance": "Swords Dance",
  "move.synthesis": "Synthesis",
  "move.tackle": "Tackle",
  "move.tail-whip": "Tail Whip",
  "move.tailwind": "Tailwind",
  "move.take-down": "Take Down",
  "move.tearful-look": "Tearful Look",
  "move.telekinesis": "Telekinesis",
  "move.teleport": "Teleport",
  "move.thief": "Thief",
  "move.throat-chop": "Throat Chop",
  "move.thunder": "Thunder",
  "move.thunder-punch": "Thunder Punch",
  "move.thunder-shock": "Thunder Shock",
  "move.thunder-wave": "Thunder Wave",
  "move.thunderbolt": "Thunderbolt",
  "move.trailblaze": "Trailblaze",
  "move.tri-attack": "Tri Attack",
  "move.trick": "Trick",
  "move.trick-room": "Trick Room",
  "move.triple-axel": "Triple Axel",
  "move.trop-kick": "Trop Kick",
  "move.twister": "Twister",
  "move.vine-whip": "Vine Whip",
  "move.volt-switch": "Volt Switch",
  "move.volt-tackle": "Volt Tackle",
  "move.water-gun": "Water Gun",
  "move.water-pulse": "Water Pulse",
  "move.water-shuriken": "Water Shuriken",
  "move.water-spout": "Water Spout",
  "move.waterfall": "Waterfall",
  "move.whirlpool": "Whirlpool",
  "move.wicked-blow": "Wicked Blow",
  "move.wide-guard": "Wide Guard",
  "move.wild-charge": "Wild Charge",
  "move.will-o-wisp": "Will-o-Wisp",
  "move.wing-attack": "Wing Attack",
  "move.wish": "Wish",
  "move.wood-hammer": "Wood Hammer",
  "move.x-scissor": "X-Scissor",
  "move.yawn": "Yawn",
  "move.zap-cannon": "Zap Cannon",
  "move.zen-headbutt": "Zen Headbutt"
});
// @end move-names-i18n

export default names;
